import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, FileText, GraduationCap, ShieldCheck, 
  ArrowRight, CheckCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (error) {
        console.error('Error seeding data:', error);
      }
    };
    seedData();
  }, []);

  const services = [
    {
      icon: Users,
      title: 'Personel Temini',
      description: 'Mavi yaka ve beyaz yaka tüm vasıflarda profesyonel personel temin hizmeti.',
      color: '#2A9D8F',
    },
    {
      icon: FileText,
      title: 'Bordrolama',
      description: 'SGK ve özlük işlemleri dahil eksiksiz bordrolama hizmeti.',
      color: '#E76F51',
    },
    {
      icon: GraduationCap,
      title: 'Eğitim Danışmanlığı',
      description: 'Çalışan gelişimi için kapsamlı eğitim programları ve danışmanlık.',
      color: '#E9C46A',
    },
    {
      icon: ShieldCheck,
      title: 'İSG Danışmanlığı',
      description: '6331 sayılı kanun kapsamında iş sağlığı ve güvenliği danışmanlığı.',
      color: '#2A9D8F',
    },
  ];

  const stats = [
    { number: '200+', label: 'Mutlu Müşteri' },
    { number: '3.000+', label: 'Yerleştirilen Personel' },
    { number: '5+', label: 'Yıllık Deneyim' },
    { number: '95%', label: 'Müşteri Memnuniyeti' },
  ];

  const features = [
    '4857 Sayılı İş Kanunu\'na tam uyum',
    '6331 Sayılı İSG Kanunu kapsamında hizmet',
    'Günlük, dönemsel ve aylık personel temini',
    'Profesyonel özlük ve SGK işlemleri',
    'Kapsamlı ön eğitim programları',
    'Liyakat odaklı personel seçimi',
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 hero-gradient"></div>
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#2A9D8F]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-[#E76F51]/10 rounded-full blur-3xl"></div>
        
        <div className="section-container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <span className="inline-block px-4 py-2 bg-[#2A9D8F]/10 text-[#2A9D8F] rounded-full text-sm font-medium mb-6">
                İnsan Kaynakları Çözümleri
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#264653] leading-tight mb-6 font-['Outfit']">
                İşgücü İhtiyaçlarınız İçin
                <span className="text-[#2A9D8F]"> Profesyonel</span> Çözümler
              </h1>
              <p className="text-lg text-[#264653]/70 leading-relaxed mb-8 max-w-lg">
                Personel temini, bordrolama ve İK danışmanlığı hizmetleriyle firmanızın 
                asıl hedefine odaklanmasını sağlıyoruz.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/iletisim" className="btn-primary flex items-center gap-2" data-testid="hero-cta">
                  Teklif Al <ArrowRight size={18} />
                </Link>
                <Link to="/hizmetler" className="btn-secondary" data-testid="hero-services-btn">
                  Hizmetlerimiz
                </Link>
              </div>
            </div>

            <div className="relative animate-fade-in-up animation-delay-200">
              <div className="relative">
                <img
                  src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg"
                  alt="Profesyonel ekip"
                  className="rounded-3xl shadow-2xl w-full object-cover h-[500px]"
                />
                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#2A9D8F]/10 rounded-full flex items-center justify-center">
                      <Users className="text-[#2A9D8F]" size={24} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#264653] font-['Outfit']">3.000+</p>
                      <p className="text-sm text-[#264653]/60">Yerleştirilen Personel</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-spacing bg-white" data-testid="services-section">
        <div className="section-container">
          <div className="text-center mb-16">
            <span className="text-[#2A9D8F] font-medium text-sm uppercase tracking-wider">
              Hizmetlerimiz
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#264653] mt-4 font-['Outfit']">
              Size Nasıl Yardımcı Olabiliriz?
            </h2>
            <p className="text-[#264653]/70 mt-4 max-w-2xl mx-auto">
              İnsan kaynakları alanında kapsamlı çözümlerimizle işletmenizin verimliliğini artırıyoruz.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="service-card group cursor-pointer"
                data-testid={`service-card-${index}`}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-colors"
                  style={{ backgroundColor: `${service.color}15` }}
                >
                  <service.icon size={28} style={{ color: service.color }} />
                </div>
                <h3 className="text-xl font-semibold text-[#264653] mb-3 font-['Outfit']">
                  {service.title}
                </h3>
                <p className="text-[#264653]/70 leading-relaxed">{service.description}</p>
                <Link
                  to="/hizmetler"
                  className="inline-flex items-center gap-2 text-[#2A9D8F] font-medium mt-4 group-hover:gap-3 transition-all"
                >
                  Detaylı Bilgi <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#2A9D8F]" data-testid="stats-section">
        <div className="section-container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <p className="text-4xl md:text-5xl font-bold text-white font-['Outfit']">
                  {stat.number}
                </p>
                <p className="text-white/80 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="section-spacing" data-testid="why-us-section">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#2A9D8F] font-medium text-sm uppercase tracking-wider">
                Neden Biz?
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#264653] mt-4 mb-8 font-['Outfit']">
                Güvenilir Çözüm Ortağınız
              </h2>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="text-[#2A9D8F] shrink-0 mt-1" size={20} />
                    <p className="text-[#264653]/80">{feature}</p>
                  </div>
                ))}
              </div>
              <Link
                to="/hakkimizda"
                className="btn-primary inline-flex items-center gap-2 mt-8"
                data-testid="about-cta"
              >
                Daha Fazla Bilgi <ArrowRight size={18} />
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://images.pexels.com/photos/5926382/pexels-photo-5926382.jpeg"
                alt="Profesyonel danışmanlık"
                className="rounded-3xl shadow-xl w-full object-cover h-[450px]"
              />
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#E76F51]/20 rounded-full"></div>
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#2A9D8F]/20 rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-[#E76F51]" data-testid="cta-section">
        <div className="section-container text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit']">
            Hemen Teklif Alın
          </h2>
          <p className="text-white/90 max-w-xl mx-auto mb-8">
            İnsan kaynakları ihtiyaçlarınız için size özel çözümler sunalım. 
            Uzman ekibimizle iletişime geçin.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/iletisim"
              className="h-12 px-8 rounded-full font-medium transition-all bg-white text-[#E76F51] hover:bg-white/90 flex items-center gap-2"
              data-testid="cta-contact"
            >
              İletişime Geç <ArrowRight size={18} />
            </Link>
            <a
              href="tel:02169989030"
              className="h-12 px-8 rounded-full font-medium transition-all border-2 border-white text-white hover:bg-white hover:text-[#E76F51] flex items-center gap-2"
            >
              0216 998 90 30
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
